// Peak-season reading, kept free of any data import so a page can answer "is it tick
// season right now?" without pulling in states.ts or the map layers.

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

const MONTH_NAMES = MONTHS.map((m) => m[0].toUpperCase() + m.slice(1));

export interface ParsedSeason {
  /** 1-12, or null when the entry doesn't name a starting month. */
  startMonth: number | null;
  endMonth: number | null;
  yearRound: boolean;
}

/**
 * Read a StateInfo.peakSeason string. The entries are written for humans
 * ("April – October", "Year-round (peak spring–fall)", "Spring–fall (limited local
 * data)"), so the parser only trusts explicit month names and reports null rather
 * than guessing a month from a season word.
 */
export function parsePeakSeason(text: string): ParsedSeason {
  const lower = text.toLowerCase();
  const yearRound = lower.includes("year-round") || lower.includes("year round");
  const found: number[] = [];
  for (const m of lower.matchAll(/[a-z]+/g)) {
    const i = MONTHS.indexOf(m[0]);
    if (i >= 0) found.push(i + 1);
  }
  return {
    startMonth: found.length ? found[0] : null,
    endMonth: found.length > 1 ? found[found.length - 1] : null,
    yearRound,
  };
}

export type SeasonStatus =
  | { kind: "year-round" }
  | { kind: "in"; endMonth: string }
  | { kind: "out"; startMonth: string }
  /** The entry names no months, so we say nothing rather than guess. */
  | { kind: "unknown" };

/** Where `date` falls against a peak-season string. Handles windows that wrap the year. */
export function seasonStatus(peakSeason: string, date: Date): SeasonStatus {
  const { startMonth, endMonth, yearRound } = parsePeakSeason(peakSeason);
  if (yearRound) return { kind: "year-round" };
  if (startMonth === null || endMonth === null) return { kind: "unknown" };
  const m = date.getMonth() + 1;
  const inside =
    startMonth <= endMonth ? m >= startMonth && m <= endMonth : m >= startMonth || m <= endMonth;
  return inside
    ? { kind: "in", endMonth: MONTH_NAMES[endMonth - 1] }
    : { kind: "out", startMonth: MONTH_NAMES[startMonth - 1] };
}

/** One plain sentence for a season status, or null when there is nothing honest to say. */
export function seasonSentence(status: SeasonStatus): string | null {
  switch (status.kind) {
    case "year-round":
      return "Ticks can be active in any month here.";
    case "in":
      return `It's tick season now. Peak activity usually lasts through ${status.endMonth}.`;
    case "out":
      return `It's outside peak season, which usually starts in ${status.startMonth}. Ticks can still bite on warm days.`;
    case "unknown":
      return null;
  }
}

export type MonthActivity = "peak" | "active" | null;

/**
 * Twelve months, January first, for a season chart: "peak" inside the named window,
 * "active" for the rest of a year-round season, null otherwise. An entry with no month
 * names ("Not established here") gives twelve nulls rather than a guess.
 */
export function seasonMonths(peakSeason: string): MonthActivity[] {
  const { startMonth, endMonth, yearRound } = parsePeakSeason(peakSeason);
  return Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    if (startMonth !== null && endMonth !== null) {
      const inside =
        startMonth <= endMonth
          ? m >= startMonth && m <= endMonth
          : m >= startMonth || m <= endMonth;
      if (inside) return "peak";
    }
    return yearRound ? "active" : null;
  });
}

/** "Jan" … "Dec", and the full names, for chart headings and their accessible text. */
export const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));
export { MONTH_NAMES };

/**
 * How many of these seasons are inside their peak window in `date`'s month. A year-round
 * season counts only during its named peak: Florida's ticks are active in October, but
 * its peak is April to August, and "peak season is under way" should mean the peak.
 */
export function inPeakCount(peakSeasons: readonly string[], date: Date): number {
  const m = date.getMonth();
  return peakSeasons.filter((p) => seasonMonths(p)[m] === "peak").length;
}
